import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { AccountingPeriodService } from '../../../accounting/service/data/accounting-period.service';
import { LocalDate } from '../../../core/model/local-date';
import { ActionWindowService } from '../../../core/service/action-window.service';
import { FormControlExFactory } from '../../../core/service/factory/form-control-ex-factory.service';
import { AccountingPeriodCloseResult, AccountingPeriodReopenInfo } from '../../../accounting/model/opening-balance-run';
import { Company } from '../../model/company';
import { SelectionItem } from '../../../core/component/select/select.component';
import { LanguageService } from '../../../core/service/language.service';

@Component({
  templateUrl: './set-lock-date.component.html',
  styleUrls: ['./set-lock-date.component.scss'],
  providers: [AccountingPeriodService],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class SetLockDateComponent implements OnInit {
  constructor(
    private _dialogService: ActionWindowService,
    private _fb: FormBuilder,
    private _formControlFactory: FormControlExFactory,
    private _service: AccountingPeriodService
  ) {}

  @Input() dataContext: { company?: Company; companies?: Company[]; mode: 'close' | 'reopen' };

  form: FormGroup;
  periodDate: LocalDate;
  reopenInfo: AccountingPeriodReopenInfo;
  reopenInfoLoadFailed = false;
  months: SelectionItem<number>[] = [
    new SelectionItem('MONTH.JANUARY', 1),
    new SelectionItem('MONTH.FEBRUARY', 2),
    new SelectionItem('MONTH.MARCH', 3),
    new SelectionItem('MONTH.APRIL', 4),
    new SelectionItem('MONTH.MAY', 5),
    new SelectionItem('MONTH.JUNE', 6),
    new SelectionItem('MONTH.JULY', 7),
    new SelectionItem('MONTH.AUGUST', 8),
    new SelectionItem('MONTH.SEPTEMBER', 9),
    new SelectionItem('MONTH.OCTOBER', 10),
    new SelectionItem('MONTH.NOVEMBER', 11),
    new SelectionItem('MONTH.DECEMBER', 12),
  ];

  get isReopen(): boolean {
    return this.dataContext?.mode === 'reopen';
  }

  get titleKey(): string {
    return this.isReopen ? 'ACCOUNTING_PERIOD.REOPEN_HEADER' : 'ACCOUNTING_PERIOD.CLOSE_HEADER';
  }

  get descriptionKey(): string {
    return this.isReopen ? 'ACCOUNTING_PERIOD.REOPEN_DESCRIPTION' : 'ACCOUNTING_PERIOD.CLOSE_DESCRIPTION';
  }

  get companies(): Company[] {
    return this.dataContext?.companies?.length ? this.dataContext.companies : this.dataContext?.company ? [this.dataContext.company] : [];
  }

  get isMany(): boolean {
    return this.companies.length > 1;
  }

  get currentAccountingClosedTo(): LocalDate {
    return this.companies.reduce((latestClosedTo: LocalDate, company: Company) => {
      const accountingClosedTo = company?.accountingClosedTo;
      if (!accountingClosedTo?.hasValue()) return latestClosedTo;
      return !latestClosedTo || LocalDate.GreaterThan(accountingClosedTo, latestClosedTo) ? accountingClosedTo : latestClosedTo;
    }, null);
  }

  get currentAccountingClosedToText(): string {
    return this.formatPeriod(this.currentAccountingClosedTo);
  }

  get currentAccountingClosedToKey(): string {
    return this.isMany ? 'ACCOUNTING_PERIOD.LATEST_CLOSED_TO' : 'ACCOUNTING_PERIOD.CURRENTLY_CLOSED_TO';
  }

  get selectableMonths(): SelectionItem<number>[] {
    const firstOpenPeriod = this.getFirstOpenPeriod();
    const selectedYear = Number(this.form?.controls['year']?.value);

    if (!firstOpenPeriod || !Number.isInteger(selectedYear) || selectedYear > firstOpenPeriod.year) return this.months;
    if (selectedYear < firstOpenPeriod.year) return [];
    return this.months.filter(month => month.value >= firstOpenPeriod.month);
  }

  get isSelectedPeriodAlreadyClosed(): boolean {
    if (this.isReopen || !this.form || !this.currentAccountingClosedTo || this.form.invalid) return false;
    return !LocalDate.GreaterThan(this.getCloseDate(), this.currentAccountingClosedTo);
  }

  get isSelectedPeriodCurrentOrFuture(): boolean {
    if (this.isReopen || !this.form || this.form.invalid || this.isSelectedPeriodAlreadyClosed) return false;

    const selectedPeriod = this.getCloseDate();
    const today = LocalDate.today();
    return selectedPeriod.year > today.year || (selectedPeriod.year === today.year && selectedPeriod.month >= today.month);
  }

  get alreadyClosedCompanies(): Company[] {
    if (this.isReopen || !this.form) return [];

    const closeDate = this.getCloseDate();
    return this.companies.filter(company => this.isAlreadyClosed(company, closeDate));
  }

  get companiesToClose(): Company[] {
    if (this.isReopen || !this.form) return this.companies;

    const closeDate = this.getCloseDate();
    return this.companies.filter(company => !this.isAlreadyClosed(company, closeDate));
  }

  cancel() {
    this._dialogService.close();
  }

  ngOnInit() {
    this.periodDate =
      this.isReopen && this.dataContext.company?.accountingClosedTo?.hasValue()
        ? this.dataContext.company.accountingClosedTo.clone()
        : LocalDate.today();

    const formConfig: any = {};

    if (this.isReopen) {
      formConfig.periodDate = this._formControlFactory.createReadonly('ACCOUNTING_PERIOD.CLOSED_TO_DATE', this.periodDate);
      formConfig.reason = this._formControlFactory.createMaxLength('ACCOUNTING_PERIOD.REASON', '', 255);
      formConfig.confirmed = this._formControlFactory.createMandatory('ACCOUNTING_PERIOD.CONFIRMED', false);

      if (this.dataContext.company?.id) {
        this._service.getReopenInfo(this.dataContext.company.id).subscribe(info => {
          this.reopenInfo = info;
          this.reopenInfoLoadFailed = !info;
        });
      }
    } else {
      const today = LocalDate.today();
      const defaultPeriod = this.getDefaultClosePeriod(today);
      const firstOpenPeriod = this.getFirstOpenPeriod();
      formConfig.year = this._formControlFactory.createWithOptions('COMMON.YEAR', defaultPeriod.year, {
        required: true,
        minValue: firstOpenPeriod?.year ?? 1900,
        maxValue: 2200,
      });
      formConfig.month = this._formControlFactory.createMandatory('COMMON.MONTH', defaultPeriod.month);
      formConfig.description = this._formControlFactory.createMaxLength('ACCOUNTING_PERIOD.DESCRIPTION', '', 255);
    }

    this.form = this._fb.group(formConfig);
  }

  private _working = false;

  get working(): boolean {
    return this._working;
  }

  onSubmit() {
    if (!this.canSubmit()) return;

    this._working = true;
    if (this.isReopen) {
      this._service
        .reopen(
          this.dataContext.company.id,
          this.form.controls['periodDate'].value,
          this.form.controls['reason'].value,
          this.form.controls['confirmed'].value
        )
        .subscribe(
          res => {
            if (res) {
              // Reverting the latest closing leaves the period closed through the run below it,
              // which is what reopen-info already reported as the remaining closed-to date.
              this.dataContext.company.accountingClosedTo = this.reopenInfo?.remainingClosedToDate ?? LocalDate.None;
              this._dialogService.complete(res);
            } else {
              this._working = false;
            }
          },
          () => {
            this._working = false;
          }
        );
      return;
    }

    if (this.isMany) {
      this._service
        .closeMany(
          this.companies.map(company => company.id),
          this.getCloseDate(),
          this.form.controls['description'].value
        )
        .subscribe(
          res => {
            if (res) {
              this.applyBatchResult(res);
              this._dialogService.complete(res);
            } else {
              this._working = false;
            }
          },
          () => {
            this._working = false;
          }
        );
      return;
    }

    this._service.close(this.companies[0].id, this.getCloseDate(), this.form.controls['description'].value).subscribe(
      res => {
        if (res) {
          this._dialogService.complete(res);
        } else {
          this._working = false;
        }
      },
      () => {
        this._working = false;
      }
    );
  }

  canSubmit(): boolean {
    if (this.form.invalid || this._working) return false;
    if (this.isReopen) return this.reopenInfo?.canReopen === true && this.form.controls['confirmed'].value;
    return !this.isSelectedPeriodAlreadyClosed && this.companiesToClose.length > 0;
  }

  private getDefaultClosePeriod(today: LocalDate): LocalDate {
    const defaultPeriod = today.clone();
    defaultPeriod.addMonths(-1);

    const firstOpenPeriod = this.getFirstOpenPeriod();
    if (firstOpenPeriod && LocalDate.GreaterThan(firstOpenPeriod, defaultPeriod)) return firstOpenPeriod;
    return defaultPeriod;
  }

  private getFirstOpenPeriod(): LocalDate {
    if (!this.currentAccountingClosedTo) return null;

    const firstOpenPeriod = this.currentAccountingClosedTo.clone();
    firstOpenPeriod.addMonths(1);
    firstOpenPeriod.day = LocalDate.daysInMonth(firstOpenPeriod.year, firstOpenPeriod.month);
    return firstOpenPeriod;
  }

  private formatPeriod(date: LocalDate): string {
    if (!date?.hasValue()) return '';
    return date.toDate().toLocaleDateString(LanguageService.getCultureLang(), { month: 'long', year: 'numeric' });
  }

  private isAlreadyClosed(company: Company, closeDate: LocalDate): boolean {
    const accountingClosedTo = company?.accountingClosedTo;
    return (
      accountingClosedTo?.hasValue() &&
      (LocalDate.Equal(accountingClosedTo, closeDate) || LocalDate.GreaterThan(accountingClosedTo, closeDate))
    );
  }

  private getCloseDate(): LocalDate {
    const year = Number(this.form.controls['year'].value);
    const month = Number(this.form.controls['month'].value);
    return new LocalDate(year, month, LocalDate.daysInMonth(year, month));
  }

  private applyBatchResult(result: AccountingPeriodCloseResult): void {
    result.closedRuns.forEach(run => {
      const company = this.companies.find(item => item.id === run.companyId);
      if (company) {
        company.accountingClosedTo = run.closedToDate;
      }
    });
  }
}
